import moment, { Moment } from 'moment';
import {
  ISuiteArea,
  ISuiteAsset,
  ProcessedSuite,
} from 'src/pages/Suite/Components/UnitImportDialogue/Hooks/BulkSaveSuiteAPITypes';
import { IndividualSuiteParsedFromCSV } from '../BulkSaveSuiteAPITypes';
import { OPTIONAL_UNIT_COLUMNS } from 'src/pages/Suite/Components/UnitImportDialogue/Hooks/Helpers/config';
import { ModulePermission } from 'src/components/ContactForm/types';

export interface CatalogueMismatchError {
  unmatchedCsvAssets: string[];
  catalogueItemNames: string[];
}

export interface ParkingPermissionCheckResult {
  hasPermissionError: boolean;
  unitNumbersWithParkingOrStorage: string[];
}

export interface CatalogueItem {
  id: string;
  name: string;
}

export interface UnitRowValidationError {
  row: number;
  unitNumber: string;
  issues: string[];
}

const ACCEPTED_DATE_FORMATS = [
  'YYYY-MM-DD',
  'MM-DD-YYYY',
  'MM/DD/YYYY',
  'M/D/YYYY',
  'M-D-YYYY',
];

const OUTPUT_DATE_FORMAT = 'YYYY-MM-DD';

const MANDATORY_ISSUE_PATTERNS: string[] = [
  'Unit number is required',
  'Building is required',
];

export interface ClassifiedValidationResult {
  errors: UnitRowValidationError[];
  warnings: UnitRowValidationError[];
}

const isMandatoryIssue = (issue: string): boolean => {
  return MANDATORY_ISSUE_PATTERNS.some((pattern: string) => {
    return issue.includes(pattern);
  });
};

export const classifyValidationErrors = (
  postErrors: UnitRowValidationError[]
): ClassifiedValidationResult => {
  const errors: UnitRowValidationError[] = [];
  const warnings: UnitRowValidationError[] = [];

  for (const entry of postErrors) {
    const mandatoryIssues: string[] = entry.issues.filter((issue: string) => {
      return isMandatoryIssue(issue);
    });

    const optionalIssues: string[] = entry.issues.filter((issue: string) => {
      return !isMandatoryIssue(issue);
    });

    if (mandatoryIssues.length > 0) {
      errors.push({ ...entry, issues: mandatoryIssues });
    }

    if (optionalIssues.length > 0) {
      warnings.push({ ...entry, issues: optionalIssues });
    }
  }

  return { errors, warnings };
};

const isValidDateString = (val: string | undefined | null): boolean => {
  if (!val || val.trim() === '') return true;
  return moment(
    val,
    ['MM-DD-YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD', 'M/D/YYYY', 'M-D-YYYY'],
    true
  ).isValid();
};

const normalizeDateString = (val: string | undefined | null): string | null => {
  if (!val || val.trim() === '') return null;
  const parsed: Moment = moment(val.trim(), ACCEPTED_DATE_FORMATS, true);
  return parsed.isValid() ? parsed.format(OUTPUT_DATE_FORMAT) : null;
};

const validateAreas = (areas: ISuiteArea[]): string[] => {
  const issues: string[] = [];

  if (!areas || areas.length === 0) {
    issues.push('At least one area/room is required.');
    return issues;
  }

  areas.forEach((area: ISuiteArea, areaIndex: number) => {
    if (!area.Name || area.Name.trim() === '') {
      issues.push(`Area at position ${areaIndex + 1} is missing a name.`);
    }

    if (!area.Assets || area.Assets.length === 0) {
      issues.push(
        `Area "${area.Name || `#${areaIndex + 1}`}" has no assets — check your CSV is normalised correctly (see import template).`
      );
      return;
    }

    area.Assets.forEach((asset: ISuiteAsset, assetIndex: number) => {
      if (!asset.Name || asset.Name.trim() === '') {
        issues.push(
          `Asset at position ${assetIndex + 1} in area "${area.Name}" is missing a name.`
        );
      }
    });
  });

  return issues;
};

const PARKING_DETAILS_PERMISSION_NAME = 'Parking Details';

const rowHasParkingOrStorageData = (
  row: IndividualSuiteParsedFromCSV
): boolean => {
  const hasParkingStalls: boolean = Boolean(row.ParkingStalls?.trim());
  const hasStorageLockers: boolean = Boolean(row.StorageLockers?.trim());

  return hasParkingStalls || hasStorageLockers;
};

export const validateParkingStoragePermission = (
  rows: IndividualSuiteParsedFromCSV[],
  allowedTabList: ModulePermission[]
): ParkingPermissionCheckResult => {
  const passResult: ParkingPermissionCheckResult = {
    hasPermissionError: false,
    unitNumbersWithParkingOrStorage: [],
  };

  const parkingDetailsPermission: ModulePermission | undefined =
    allowedTabList.find(
      (tabItem: ModulePermission) =>
        tabItem.name === PARKING_DETAILS_PERMISSION_NAME
    );

  if (parkingDetailsPermission) {
    return passResult;
  }

  const rowsWithParkingOrStorage: IndividualSuiteParsedFromCSV[] = rows.filter(
    (row: IndividualSuiteParsedFromCSV) => rowHasParkingOrStorageData(row)
  );

  if (rowsWithParkingOrStorage.length === 0) {
    return passResult;
  }

  const trimmedNonUniqueUnitNumbers: string[] = rowsWithParkingOrStorage.map(
    (row: IndividualSuiteParsedFromCSV) => row.Number.trim()
  );

  const uniqueUnitNumbers: Set<string> = new Set(trimmedNonUniqueUnitNumbers);

  const unitNumbersWithParkingOrStorage: string[] =
    Array.from(uniqueUnitNumbers);

  const failResult = {
    hasPermissionError: true,
    unitNumbersWithParkingOrStorage,
  };

  return failResult;
};

export const validateCatalogueAssets = (
  suites: ProcessedSuite[],
  catalogueItems: CatalogueItem[]
): CatalogueMismatchError | null => {
  const catalogueNames = new Set<string>(
    catalogueItems.map((item: CatalogueItem) => item.name.trim().toLowerCase())
  );

  const uniqueCsvAssetNames = new Set<string>();

  suites.forEach((suite: ProcessedSuite) => {
    suite.Areas.forEach((area: ISuiteArea) => {
      area.Assets.forEach((asset: ISuiteAsset) => {
        if (asset.Name?.trim()) {
          uniqueCsvAssetNames.add(asset.Name.trim());
        }
      });
    });
  });

  const unmatchedCsvAssets: string[] = Array.from(uniqueCsvAssetNames).filter(
    (name: string) => !catalogueNames.has(name.toLowerCase())
  );

  if (unmatchedCsvAssets.length === 0) return null;

  return {
    unmatchedCsvAssets,
    catalogueItemNames: catalogueItems.map((item: CatalogueItem) => item.name),
  };
};

export const validateProcessedSuites = (
  suites: ProcessedSuite[]
): UnitRowValidationError[] => {
  const errors: UnitRowValidationError[] = [];

  suites.forEach((suite: ProcessedSuite, index: number) => {
    const issues: string[] = [];

    if (!suite.Number || suite.Number.trim() === '') {
      issues.push('Unit number is required.');
    }

    if (!suite.Status || suite.Status.trim() === '') {
      issues.push('Status is required.');
    }

    if (!suite.Building || suite.Building.trim() === '') {
      issues.push('Building is required.');
    }

    const warrantyDate = suite.WarrantyDefaultStartDate;

    if (!warrantyDate || isNaN(new Date(warrantyDate).getTime())) {
      issues.push(
        `WarrantyDefaultStartDate is required and must be a valid date (e.g. 2025-07-16). Got: "${warrantyDate ?? 'empty'}".`
      );
    }

    // Validate then normalize dates in-place to YYYY-MM-DD.
    if (!isValidDateString(suite.OrientationDate)) {
      issues.push(
        `Orientation date "${suite.OrientationDate}" is not a recognised format. Expected YYYY-MM-DD.`
      );
    } else {
      suite.OrientationDate =
        normalizeDateString(suite.OrientationDate) ?? suite.OrientationDate;
    }

    if (!isValidDateString(suite.CompletionDate)) {
      issues.push(
        `Completion date "${suite.CompletionDate}" is not a recognised format. Expected YYYY-MM-DD.`
      );
    } else {
      suite.CompletionDate =
        normalizeDateString(suite.CompletionDate) ?? suite.CompletionDate;
    }

    if (!isValidDateString(suite.PossessionDate)) {
      issues.push(
        `Possession date "${suite.PossessionDate}" is not a recognised format. Expected YYYY-MM-DD.`
      );
    } else {
      suite.PossessionDate =
        normalizeDateString(suite.PossessionDate) ?? suite.PossessionDate;
    }

    const areaIssues: string[] = validateAreas(suite.Areas);
    issues.push(...areaIssues);

    if (issues.length > 0) {
      errors.push({
        row: index + 1,
        unitNumber: suite.Number || '(empty)',
        issues,
      });
    }
  });

  return errors;
};

export interface RawRowValidationError {
  row: number;
  unitNumber: string;
  issues: string[];
}

interface UnitGroupEntry {
  building: string;
  rows: Array<{ room: string; rowIndex: number }>;
}

const parseHeaderSet = (fileText: string): Set<string> => {
  const firstNewlineIndex: number = fileText.indexOf('\n');
  const headerLine: string = fileText
    .substring(0, firstNewlineIndex)
    .replace(/\r/g, '');

  const headers: string[] = headerLine.split(',').map((header: string) => {
    return header.trim().toLowerCase();
  });

  return new Set(headers);
};

export const extractOptionalMissingColumns = (
  fileText: string
): string[] => {
  const detectedHeaders: Set<string> = parseHeaderSet(fileText);

  return OPTIONAL_UNIT_COLUMNS.filter(
    (col: string) => !detectedHeaders.has(col.trim().toLowerCase())
  );
};

export const extractPresentOptionalColumns = (
  fileText: string
): string[] => {
  const detectedHeaders: Set<string> = parseHeaderSet(fileText);

  return OPTIONAL_UNIT_COLUMNS.filter(
    (col: string) => detectedHeaders.has(col.trim().toLowerCase())
  );
};

export const collectUniqueCsvAssetNames = (
  suites: ProcessedSuite[]
): string[] => {
  const uniqueNames = new Set<string>();

  suites.forEach((suite: ProcessedSuite) => {
    suite.Areas.forEach((area: ISuiteArea) => {
      area.Assets.forEach((asset: ISuiteAsset) => {
        if (asset.Name?.trim()) {
          uniqueNames.add(asset.Name.trim());
        }
      });
    });
  });

  return Array.from(uniqueNames);
};

export const validateRawUnitRows = (
  rows: IndividualSuiteParsedFromCSV[]
): RawRowValidationError[] => {
  const errors: RawRowValidationError[] = [];

  // Map of unitNumber -> UnitGroupEntry. Used to catch building mismatches and duplicate room names per unit
  const unitGroups = new Map<string, UnitGroupEntry>();

  rows.forEach((row: IndividualSuiteParsedFromCSV, index: number) => {
    const unitNumber: string = (row.Number || '').trim();
    const building: string = (row.Building || '').trim();
    const room: string = (row.Rooms || row.Areas || '').trim();
    const rowIssues: string[] = [];

    if (!unitNumber) {
      return;
    }

    const existing: UnitGroupEntry | undefined = unitGroups.get(unitNumber);

    if (!existing) {
      unitGroups.set(unitNumber, {
        building,
        rows: [{ room, rowIndex: index }],
      });
      return;
    }

    // Same unit number but different building — almost certainly a data error
    if (existing.building.toLowerCase() !== building.toLowerCase()) {
      rowIssues.push(
        `Unit "${unitNumber}" appears with building "${building}" here but was "${existing.building}" in a previous row. ` +
          `Each unit number must belong to exactly one building.`
      );
    }

    // Same unit + same room name appearing more than once — assets from this row would be silently dropped
    const duplicateRoom = existing.rows.find(
      (r: any) => r.room.toLowerCase() === room.toLowerCase()
    );

    if (duplicateRoom) {
      rowIssues.push(
        `Unit "${unitNumber}" already has a "${room}" room defined at row ${duplicateRoom.rowIndex + 1}. ` +
          `Duplicate rooms cause assets to be lost — merge the assets into a single row or rename the room.`
      );
    }

    existing.rows.push({ room, rowIndex: index });

    if (rowIssues.length > 0) {
      errors.push({
        row: index + 1,
        unitNumber,
        issues: rowIssues,
      });
    }
  });

  return errors;
};


